import { useEffect, useState } from 'react';

import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import { Button } from 'Components/LegacyButton';
import { MetaData } from 'Components/MetaData';
import ScaleLoader from 'Components/ScaleLoader';

import Blog from 'Blocks/Blog';
import { Footer } from 'Blocks/Footer';
import SecondaryNavBar from 'Blocks/SecondaryNavBar/SecondaryNavBar';

import { Header } from 'Containers/Header';
import ScrollTracker from 'Containers/ScrollTracker';

import {
  blog,
  cta,
  getModuleHero,
  titleHeroPostPartum,
} from 'assets/content/landingPages/supplements';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { useFetchProductsList } from 'dux/products/hooks';

import { ModuleClinical } from './ModuleClinical/ModuleClinical';
import { ModuleHero } from './ModuleHero/ModuleHero';
import { CTA } from './CTA';
import { ModuleBigHands } from './ModuleBigHands';
import { ModuleCapsule } from './ModuleCapsule';
import { ModuleCloudyPill } from './ModuleCloudyPill';
import { ModuleFAQ } from './ModuleFAQ';
import { ModuleFormula } from './ModuleFormula';
import { ModuleFullRoutine } from './ModuleFullRoutine';
import { ModuleIngredients } from './ModuleIngredients';
import { ModuleScrollingText } from './ModuleScrollingText';
import { ModuleSelector } from './ModuleSelector';
import { ModuleTestimonials } from './ModuleTestimonials';
import { supplementsAllVariants, VIDEO_PP } from './utils';

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: calc(100vh);
`;

const Wrapper = styled.div`
  position: relative;
  color: ${theme.colors.primary[400]};
`;

const BlogWrapper = styled.section`
  padding: 104px 0 113px;

  background-color: ${theme.colors.neutral[300]};

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 93px 48px 94px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    padding: 120px 111px 144px;
  }
`;

const useStyles = makeStyles(() => ({
  button: {
    position: 'fixed',
    zIndex: 1000,
    bottom: 0,
    width: '100vw',
    height: '55px',
    margin: 0,
    lineHeight: '61px',
  },
}));

type SupplementsLandingProps = {
  header?: boolean;
  footer?: boolean;
  variant?:
    | 'before-after'
    | 'before-after-search'
    | 'rootsource1'
    | 'rootsource2'
    | 'videostraight'
    | 'videocurly'
    | 'video3'
    | 'pp';
};

const SupplementsLanding = ({ header = true, footer = true, variant }: SupplementsLandingProps) => {
  const { isMobile, isDesktop } = useResponsiveVariant();

  const [refHero, inViewRefHero] = useInView({
    threshold: 0.1,
  });
  const [moduleFormulaPosition, setModuleFormulaPosition] = useState<'relative' | 'fixed'>(
    'relative',
  );
  const [isLoading, setIsLoading] = useState(true);

  const { classes } = useStyles();

  const intersectOption = {
    threshold: 0.2,
  };
  const [refAboutSection, inViewRefAboutSection] = useInView(intersectOption);
  const [refClinicalSection, inViewRefClinicalSection] = useInView(intersectOption);
  const [refIngredientsSection, inViewRefIngredientsSection] = useInView({
    ...intersectOption,
    threshold: 0.1,
  });
  const [refHowSection, inViewRefHowSection] = useInView({
    ...intersectOption,
    threshold: isMobile ? 0.05 : 0.2,
  });
  const [refFaqSection, inViewRefFaqSection] = useInView(intersectOption);
  const navEntries = [
    {
      testId: 'suppLpAbout',
      label: 'About our supplements',
      isActive: inViewRefAboutSection,
      id: 'about',
    },
    {
      testId: 'suppLpClinical',
      label: 'Clinical results',
      isActive: inViewRefClinicalSection,
      id: 'clinical',
    },
    {
      testId: 'suppLpIngredients',
      label: 'Ingredients',
      isActive: inViewRefIngredientsSection,
      id: 'formula',
    },
    {
      testId: 'suppLpHow',
      label: 'How Prose works',
      isActive: inViewRefHowSection,
      id: 'how-prose-works',
    },
    { testId: 'suppLpFaq', label: 'FAQs', isActive: inViewRefFaqSection, id: 'faq' },
  ];

  const isAuthorizatedVariant = variant ? supplementsAllVariants.includes(variant) : false;
  const isModuleFormulaPositionFixed = moduleFormulaPosition === 'fixed';

  const isVariantPostPartum = variant === VIDEO_PP;
  const moduleHero = getModuleHero();
  const newModuleHero = isVariantPostPartum
    ? {
        ...moduleHero,
        title: titleHeroPostPartum,
      }
    : moduleHero;

  const handleModuleFormulaPosition = () => {
    if (!inViewRefHero) {
      setModuleFormulaPosition('relative');
    } else {
      setModuleFormulaPosition('fixed');
    }
  };

  useFetchProductsList();

  useEffect(() => {
    if (!isLoading) {
      handleModuleFormulaPosition();
    }
  }, [inViewRefHero, isLoading]);

  /**
   * Trick to improve the visual loading of the page
   * We wait for the image of the first module to load
   * before displaying the page
   * and also before setting the seconde module position
   * which can cause an overlapping jerky effect
   */
  const moduleHeroLoaded = () => {
    setIsLoading(false);
  };

  return (
    <>
      <MetaData
        description="Do hair vitamins work? What vitamins are good for hair growth? Find answers here and why Prose Root Source custom hair supplements are the clear choice."
        inheritParentCanonicalURL
        title="Root Source® FAQs: About Our Custom Hair Supplements | Prose"
      />

      <Wrapper>
        <ScrollTracker>
          {header && <Header banner />}

          {/* Display loader at the top of the page
      until the background image of the first module is loaded
      to avoid an overlapping jerky effect */}
          {isLoading && (
            <LoaderWrapper className="remove-in-percy">
              <ScaleLoader />
            </LoaderWrapper>
          )}

          <ModuleHero
            ref={refHero}
            bgLoaded={() => moduleHeroLoaded()}
            content={newModuleHero}
            showArrow={isModuleFormulaPositionFixed}
            variant={isAuthorizatedVariant ? variant : undefined}
          />
          <ModuleFormula position={moduleFormulaPosition} />

          {!isModuleFormulaPositionFixed && (
            <SecondaryNavBar
              dataTestId="suppLpNavSecondary"
              entries={navEntries}
              scrollMarginTop={isMobile ? '54px' : '95px'}
            />
          )}

          <section ref={refAboutSection} data-testid="suppLpAbout" id="about">
            <ModuleCapsule showCircle={!isModuleFormulaPositionFixed} />
          </section>
          <section ref={refClinicalSection} data-testid="suppLpClinical" id="clinical">
            <ModuleClinical variant={isAuthorizatedVariant ? variant : undefined} />
            <ModuleTestimonials />
            <ModuleScrollingText />
          </section>
          <section ref={refIngredientsSection} data-testid="suppLpIngredients" id="formula">
            <ModuleSelector />
            <ModuleIngredients />
            <ModuleCloudyPill />
            <ModuleBigHands />
          </section>
          <section ref={refHowSection} data-testid="suppLpHow" id="how-prose-works">
            <ModuleFullRoutine />
          </section>
          <section ref={refFaqSection} data-testid="suppLpFaq">
            <ModuleFAQ />
            <BlogWrapper data-testid="supplements-landing-module">
              <Blog backgroundColor={theme.colors.neutral[300]} content={blog} />
            </BlogWrapper>
          </section>

          {!isLoading && !isModuleFormulaPositionFixed && !isDesktop && (
            <CTA>
              {({ dataClick, to }) => (
                <Button
                  classes={{
                    root: classes.button ?? '',
                  }}
                  Component={Link}
                  data-click={dataClick}
                  data-from="supplements-sticky-cta"
                  data-testid="supplements-sticky-cta"
                  to={to}
                  variant="yellow"
                >
                  {cta}
                </Button>
              )}
            </CTA>
          )}
          {footer && <Footer hideCTA />}
        </ScrollTracker>
      </Wrapper>
    </>
  );
};

export default SupplementsLanding;
